// Lead engine contract (LEADGEN-SPEC.md §3.4, §6.1, §10, §11, §12). SPEC-OWNED: builders read it, never edit it;
// propose changes in NOTES-<agent>.md. Shared by the Worker and the web app, so no Worker or DOM types here.
// Stored JSON (niche_profiles.profile_json, lead_searches.*_json) keeps the spec's field names; API rows are camelCase.
//
// Routes (§12). (A) = admin. Non-GET /api calls need `X-Requested-With: vd`. Errors are ApiError (src/shared/api.ts).
// NICHES (A)    GET  /api/leadgen/niches -> NicheProfileRow[]
//               POST /api/leadgen/niches/propose NicheProposeRequest -> NicheProposeResponse (400 on a Canadian healthcare niche)
//               PUT  /api/leadgen/niches/:key/:v NicheUpdateRequest -> NicheProfileRow (editing a non-draft creates v+1 as a draft)
//               POST /api/leadgen/niches/:key/:v/approve -> NicheProfileRow
//               POST /api/leadgen/niches/:key/:v/activate -> NicheProfileRow (409 'activation_blocked', details: ActivationBlocked)
// GEO (A)       GET  /api/leadgen/geo?country=US|CA -> GeoOption[]
// SEARCHES (A)  POST /api/leadgen/searches SearchCreateRequest -> LeadSearch
//               POST /api/leadgen/searches/:id/estimate -> SearchEstimate
//               POST /api/leadgen/searches/:id/run SearchRunRequest -> LeadSearch (409 'search_blocked', details: SearchBlocked)
//               POST /api/leadgen/searches/:id/pause | /resume | /cancel -> LeadSearch
//               PATCH /api/leadgen/searches/:id SearchPatchRequest -> LeadSearch
//               GET  /api/leadgen/searches?limit=&offset= -> Page<LeadSearch>
//               GET  /api/leadgen/searches/:id -> SearchDetail
//               GET  /api/leadgen/searches/:id/results?tier=&status=&state=&flag=&drop=&hasHook=&page=&pageSize= -> Page<LeadRow>
//               GET  /api/leadgen/searches/:id/results.csv -> text/csv (LEAD_CSV_COLUMNS, rebuilt from D1 on every download)
//               POST /api/leadgen/searches/:id/review SearchReviewRequest -> SearchReviewResponse
//               POST /api/leadgen/searches/:id/import SearchImportRequest -> SearchImportResponse
//               GET  /api/leadgen/places/:leadPlaceId/drawer -> DrawerData (read-only drawer for a Results row that may not
//                    be a business yet; integrator addition, §10 tab 4 needs it; no live check, no history)
// CHECKS (A)    GET  /api/leadgen/checks/next?niche=&detector= -> CheckNextResponse
//               GET  /api/leadgen/checks/batch?niche=&detector=in_niche&n=30 -> CheckBatchResponse (P1-15; in_niche only)
//               POST /api/leadgen/checks CheckSubmitRequest -> CheckSubmitResponse
//               GET  /api/leadgen/evals?niche= -> DetectorEval[]
// SPEND (A)     GET  /api/leadgen/spend?month=YYYY-MM -> SpendReport
//               GET  /api/leadgen/suppression -> SuppressionEntry[]    PUT /api/leadgen/suppression SuppressionUpdateRequest -> SuppressionEntry[]
//               GET  /api/leadgen/calibration?niche= -> CalibrationReport
// RESEARCH      POST /api/leadgen/research/:businessId -> ResearchResponse (setter with access to the business, or A; 20/user/day)
// APOLLO (A)    POST /api/leadgen/apollo/reveal ApolloRevealRequest -> ApolloRevealResponse
// DRAWER        GET  /api/businesses/:id/drawer?membershipId= -> DrawerData (assertBusinessAccess)
//               POST /api/businesses/:id/live-check LiveCheckRequest -> LiveCheck (Cache-Control: no-store; nothing persisted)
// HOOKS         POST /hooks/apify?task=&sig=    GET /hooks/dataforseo?task=&sig=&id=    (public, HMAC in `sig`; 200 fast)
// TOOLS         POST /api/tools/leadgen/smoke ToolsSmokeRequest -> ToolsSmokeResponse    GET /api/tools/leadgen/searches/:id -> SearchDetail
//               (Bearer ADMIN_API_TOKEN, for tools/leadgen_smoke.mjs)

import type { CallSummary, Country, DispositionCode, ImportReport, ISODate, Meeting, RecycleRound, Task } from './api';

export type { Page } from './api-core';

// =====================================================================================================================
// §3.4 core enums and shapes (verbatim)
// =====================================================================================================================

export type LeadOffer = 'front_desk' | 'website' | 'gbp';
export const LEAD_OFFERS: readonly LeadOffer[] = ['front_desk', 'website', 'gbp'];

export type SearchArea =
  | { kind: 'city'; label: string; locationQuery: string; state: string }        // "Hamilton, ON, Canada"
  | { kind: 'region'; label: string; countryCode: string; state: string };      // whole state/province (Apify splits it; DataForSEO: one Maps search per term, up to its depth)

export type SearchMode = 'smoke' | 'pilot' | 'full' | 'single';

export type LeadFlag = 'hand_pick_state' | 'two_locations' | 'suspicious_reviews' | 'has_web_vendor' | 'is_ad'
  | 'answering_service' | 'native_ai_software' | 'commercial_only' | 'provisional_detector' | 'held_temp_closed'
  | 'region_inferred';                       // service-area listing: country/state from the search area + area code + pin (P0-1)

export interface ScoreReason { signal: string; points: number; text: string }   // "+35 two phone complaints (Aug, May 2026)"

export interface SearchEstimate {
  placesLow: number; placesHigh: number; survivorsExpected: number; probe: 'places_ids' | 'caps_only';
  byVendor: Array<{ vendor: string; op: string; units: number; usd: number }>;
  totalUsd: number; suggestedBudgetUsd: number; monthSpentUsd: number; monthCapUsd: number; blockers: string[];
}

export interface SearchFunnel {
  raw: number; dropped: Record<string, number>; held: number; survivors: number;
  tiers: Record<'A' | 'B' | 'C' | 'D', number>; approved: number; imported: number; failedTasks: Record<string, number>;
}

// =====================================================================================================================
// Enums mirrored from 0009's CHECK constraints (keep in step with migrations/0009_leadgen.sql)
// =====================================================================================================================

export type Tier = 'A' | 'B' | 'C' | 'D';
/** §7.2: A >= 60, B 40-59, C 20-39, D < 20 (priority for the offer). */
export const TIER_MIN = { A: 60, B: 40, C: 20 } as const;

export type SearchStatus = 'draft' | 'estimated' | 'running' | 'paused' | 'paused_budget' | 'review' | 'imported' | 'failed' | 'canceled';
/** lead_searches.stage. S0 (estimate) and S9 (import) are synchronous requests, so they never appear here. */
export type SearchStage = 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'S6' | 'S7' | 'S8';
export const SEARCH_STAGES: readonly SearchStage[] = ['S1', 'S2', 'S3', 'S4', 'S5', 'S6', 'S7', 'S8'];
export const STAGE_LABELS: Record<SearchStage, string> = {
  S1: 'Discover', S2: 'Pull gates', S3: 'Site and line', S4: 'Measure', S5: 'Classify', S6: 'Score', S7: 'People and contacts', S8: 'Hooks and report',
};

export type LeadPlaceStatus = 'raw' | 'dropped' | 'held' | 'survivor' | 'scored' | 'approved' | 'rejected' | 'imported';

/** lead_places.drop_gate. Gate A order is §4.3 S2 (1-15); Gate B is §4.3 S3. 'not_in_niche' comes from the in-niche check. */
export type DropGate =
  | 'no_phone' | 'toll_free' | 'closed' | 'country' | 'state_closed' | 'healthcare_ca' | 'label_deny' | 'not_in_niche'
  | 'lead_gen_listing' | 'chain' | 'too_big' | 'dnc_internal' | 'suppressed' | 'already_ours' | 'dupe' | 'no_licence'
  | 'servicetitan' | 'business_lines_only' | 'toll_free_line';
export const GATE_A_ORDER: readonly DropGate[] = [
  'no_phone', 'toll_free', 'closed', 'country', 'state_closed', 'healthcare_ca', 'label_deny', 'lead_gen_listing', 'chain',
  'too_big', 'dnc_internal', 'suppressed', 'already_ours', 'dupe', 'no_licence',
];
export const GATE_B_CODES: readonly DropGate[] = ['servicetitan', 'business_lines_only', 'toll_free_line', 'chain', 'not_in_niche'];

export type LeadTaskKind =
  | 'discover' | 'collect'                                              // S1
  | 'gate_a' | 'in_niche'                                               // S2
  | 'site' | 'site_recheck' | 'line_type'                               // S3
  | 'psi' | 'reviews_post' | 'reviews_get' | 'rank_post' | 'rank_get'   // S4
  | 'ai_reviews_submit' | 'ai_reviews_collect' | 'ai_reviews_live'      // S5 (live: one place, Messages API, small runs)
  | 'score'                                                             // S6
  | 'site_people' | 'site_people_live' | 'apollo_search' | 'email_verify' // S7 (site_people: Opus 5 Batch, or live per place when small)
  | 'hooks' | 'report';                                                 // S8
export const LEAD_TASK_KINDS: readonly LeadTaskKind[] = [
  'discover', 'collect', 'gate_a', 'in_niche', 'site', 'site_recheck', 'line_type', 'psi', 'reviews_post', 'reviews_get',
  'rank_post', 'rank_get', 'ai_reviews_submit', 'ai_reviews_collect', 'ai_reviews_live', 'score', 'site_people', 'site_people_live', 'apollo_search', 'email_verify',
  'hooks', 'report',
];
export const STAGE_OF_KIND: Record<LeadTaskKind, SearchStage> = {
  // site_recheck is enqueued in S3 but held by the S5 barrier (P1-9): S4 and S5 run during its 60-minute wait, and S6
  // still scores with both fetches (W1 "broken" needs them).
  discover: 'S1', collect: 'S1', gate_a: 'S2', in_niche: 'S2', site: 'S3', site_recheck: 'S5', line_type: 'S3',
  psi: 'S4', reviews_post: 'S4', reviews_get: 'S4', rank_post: 'S4', rank_get: 'S4',
  ai_reviews_submit: 'S5', ai_reviews_collect: 'S5', ai_reviews_live: 'S5', score: 'S6', site_people: 'S7', site_people_live: 'S7',
  apollo_search: 'S7', email_verify: 'S7',
  hooks: 'S8', report: 'S8',
};
export type LeadTaskStatus = 'queued' | 'running' | 'waiting' | 'done' | 'failed' | 'canceled';

export type LedgerVendor = 'apify' | 'dataforseo' | 'google_places' | 'google_psi' | 'twilio' | 'anthropic' | 'millionverifier' | 'apollo';
export type LedgerStatus = 'reserved' | 'settled' | 'released';

/** §7.1 signal keys (lead_signals.key). */
export type SignalKey =
  | 'site_status' | 'has_website' | 'site_social_only' | 'site_basics' | 'site_paths' | 'site_text' | 'site_tech' | 'psi_mobile'
  | 'hours' | 'claimed' | 'reviews_profile' | 'phone_complaints' | 'phone_praise' | 'map_rank' | 'line_type'
  | 'area_median_reviews' | 'chain' | 'licence' | 'suspicious_reviews';
export type SignalSource = 'apify' | 'site' | 'psi' | 'dataforseo' | 'licence' | 'twilio' | 'apollo' | 'call';

/** §7.4: the 15 hook types of signals-scoring §5.1. */
export type HookType =
  | 'site_broken' | 'phone_complaints' | 'no_website' | 'social_only_site' | 'after_hours_gap' | 'site_contradiction'
  | 'open_24h_small' | 'not_mobile_friendly' | 'no_menu_on_site' | 'weekend_closed' | 'slow_mobile_site'
  | 'unclaimed_profile' | 'maps_position' | 'few_reviews_vs_area' | 'no_age_check';
export const HOOK_TYPES: readonly HookType[] = [
  'site_broken', 'phone_complaints', 'no_website', 'social_only_site', 'after_hours_gap', 'site_contradiction', 'open_24h_small',
  'not_mobile_friendly', 'no_menu_on_site', 'weekend_closed', 'slow_mobile_site', 'unclaimed_profile', 'maps_position',
  'few_reviews_vs_area', 'no_age_check',
];
export type HookUsage = 'opener' | 'mid_call' | 'meeting';
export type HookStatus = 'active' | 'withdrawn' | 'expired';
/** Drawer live status of the primary hook (§11 block 2). Computed per live check; never written to D1. */
export type HookLiveStatus = 'still_true' | 'changed' | 'unchecked';

export type ReviewRegexKind = 'complaint' | 'praise';
export type ReviewClass = 'complaint' | 'praise' | 'none' | 'model_failed';
export type PersonSourceKind = 'licence' | 'website' | 'reply_signature' | 'call' | 'apollo';
export type PersonStatus = 'verified' | 'unconfirmed';
export type EmailSourceKind = 'website' | 'apollo' | 'call';
export type EmailVerifyStatus = 'unverified' | 'valid' | 'catch_all' | 'unknown' | 'invalid';
export type SuppressionKind = 'phone' | 'domain' | 'place_id';
export type CheckVerdict = 'correct' | 'wrong' | 'unsure';
export type NicheStatus = 'draft' | 'approved' | 'active' | 'retired';
export type SiteStatus = 'ok' | 'dns' | 'tls' | 'http_4xx' | 'http_5xx' | 'timeout' | 'parked' | 'offsite_redirect';

// =====================================================================================================================
// §3.2 settings (settings table, JSON values; seeded in 0009)
// =====================================================================================================================

export type LiveCheckPolicy = 'on_open' | 'on_open_and_connect';
export type ApolloMode = 'off' | 'search_only';
/** S1 discovery vendor (SPEC §19, 2026-10-02): DataForSEO Google Maps SERP live by default; Apify stays selectable. A search
 *  pins the value it was created under (lead_searches.discovery_vendor, 0019; NULL = a pre-0019 Apify search). */
export type DiscoveryVendor = 'dataforseo' | 'apify';
export const DISCOVERY_VENDORS: readonly DiscoveryVendor[] = ['dataforseo', 'apify'];
export interface LeadgenSettings {
  monthlyBudgetUsd: number;          // leadgen_monthly_budget_usd (150)
  placesLiveMonthlyCap: number;      // leadgen_places_live_monthly_cap (1000)
  liveCheckPolicy: LiveCheckPolicy;  // leadgen_live_check_policy ("on_open")
  handPickCapPerSearch: number;      // leadgen_hand_pick_cap_per_search (25)
  recontactCooldownDays: number;     // leadgen_recontact_cooldown_days (90)
  verifyEmails: boolean;             // leadgen_verify_emails (false)
  apolloMode: ApolloMode;            // leadgen_apollo_mode ("off")
  priorityOrdering: boolean;         // leadgen_priority_ordering (true; the §14 kill rule sets it false)
  weightsVersion: string;            // leadgen_weights_version ("v1")
  discoveryVendor: DiscoveryVendor;  // leadgen_discovery_vendor ("dataforseo"; seeded in 0019)
}
export const LEADGEN_SETTING_KEYS = {
  monthlyBudgetUsd: 'leadgen_monthly_budget_usd',
  placesLiveMonthlyCap: 'leadgen_places_live_monthly_cap',
  liveCheckPolicy: 'leadgen_live_check_policy',
  handPickCapPerSearch: 'leadgen_hand_pick_cap_per_search',
  recontactCooldownDays: 'leadgen_recontact_cooldown_days',
  verifyEmails: 'leadgen_verify_emails',
  apolloMode: 'leadgen_apollo_mode',
  priorityOrdering: 'leadgen_priority_ordering',
  weightsVersion: 'leadgen_weights_version',
  discoveryVendor: 'leadgen_discovery_vendor',
} as const satisfies Record<keyof LeadgenSettings, string>;

// =====================================================================================================================
// Campaign offer -> engine offers (§3.4). Pure; used by import (S9), the queue and the drawer's switch hint.
// =====================================================================================================================

/** The engine offers whose best score counts for a campaign offer, or null when the offer family isn't scored
 *  (import refuses it). front_desk -> [front_desk]; website_seo -> max(website, gbp); website -> [website];
 *  anything containing 'seo' or 'gbp' -> [gbp]. */
export function engineOffersFor(campaignOffer: string | null | undefined): LeadOffer[] | null {
  const o = (campaignOffer ?? '').trim().toLowerCase();
  if (o === 'front_desk') return ['front_desk'];
  if (o === 'website_seo') return ['website', 'gbp'];
  if (o === 'website') return ['website'];
  if (o.includes('seo') || o.includes('gbp')) return ['gbp'];
  return null;
}

// =====================================================================================================================
// §6.1 NicheProfile (JSON in niche_profiles.profile_json; field names as the spec writes them)
// =====================================================================================================================

export type BuyerMode = 'emergency' | 'scheduled' | 'considered';
export interface ChainBrand { name: string; verified: boolean }                  // "to confirm" until PJ ticks it
export interface SoftwareVendor { name: string; host: string; effect: 'disqualify' | 'minus5' | 'minus20' | 'flag'; verified: boolean }
export type LicenceAdapter = 'agco_csv' | 'ny_ocm' | 'none';
export interface LicenceSource { jurisdiction: string; adapter: LicenceAdapter; url: string }   // jurisdiction 'CA-ON' | 'US-NY'

export interface NicheProfile {
  key: string;
  display_name: string;
  buyer_mode: BuyerMode;
  search_terms: string[];                    // 5-12
  rank_term: string;                         // "plumber"
  places_types: string[];                    // each in placeTypes.ts Table A
  label_allow: string[];
  label_deny: string[];
  exclude_name_patterns: string[];
  chain_brands: ChainBrand[];
  healthcare_ca_risk: { flag: boolean; reason: string };
  offers_ranked: Array<{ offer: LeadOffer; why: string }>;
  hours_norm: { weekday_close_threshold: string; weekend_open_expected: boolean };   // "18:00"
  emergency_trade: boolean;
  review_pain_terms: string[];
  software_vendors: SoftwareVendor[];
  licence_required: boolean;
  licence_sources: LicenceSource[];
  review_ceiling: number | null;             // 1500 for trades and auto; null for dispensaries
  size_band_reviews: [number, number];
  reason_lines: string[];
  disqualifiers: string[];
}

export interface NicheProfileRow {
  key: string;
  version: number;
  displayName: string;
  status: NicheStatus;
  profile: NicheProfile;
  proposedBy: string;                        // 'seed' | 'claude-opus-5' | 'pj'
  approvedBy: number | null;
  approvedAt: ISODate | null;
  smokeSearchId: number | null;
  pilotSearchId: number | null;
  activatedAt: ISODate | null;
  createdAt: ISODate;
  smokePassed: boolean | null;               // null = no smoke yet
  pilotPrecision: number | null;             // in-niche precision from the pilot's checks; null = not measured
  detectors: { passed: number; total: number };   // "4 of 9 passed" (opener-eligible for this niche)
  /** Owner fast path (2026-10-01): set when the spend owner activated this version with quality gates waived. */
  activationOverride?: ActivationOverride | null;
}

/** The spend owner may activate a niche without waiting for its smoke, pilot and 30 checks. The healthcare (CRTC)
 *  gate is never waivable, and every search still runs the live state rules and Gate A/B. */
export interface ActivationOverride { by: number; at: ISODate; waived: string[] }
/** POST /niches/:key/:v/activate body (optional). ownerOverride: owner only; activates a draft or approved version. */
export interface NicheActivateRequest { ownerOverride?: boolean }
/** Activation gate keys the owner fast path may waive ('state' only while no pilot ran). */
export const WAIVABLE_ACTIVATION_GATES: readonly string[] = ['smoke', 'state', 'in_niche_precision'];

export interface NicheProposeRequest { text: string; country: Country; offerHint?: LeadOffer }
/** One item the validator removed from the model's proposal (unknown place type, licence URL that didn't return 200, ...). */
export interface ProposalDrop { field: string; value: string; reason: string }
export interface NicheProposeResponse { niche: NicheProfileRow; dropped: ProposalDrop[] }
export interface NicheUpdateRequest { displayName?: string; profile: NicheProfile }
/** §6.2 step 5. 409 'activation_blocked' carries details: ActivationBlocked. */
export interface ActivationGate { key: 'in_niche_precision' | 'healthcare' | 'state' | 'smoke'; ok: boolean; detail: string }
export interface ActivationBlocked { gates: ActivationGate[] }

// =====================================================================================================================
// Geography (§10 tab 2), read live from state_rules
// =====================================================================================================================

export interface GeoOption {
  code: string;                              // 'US-GA' | 'CA-ON'
  name: string;
  group: 'open' | 'hand_pick' | 'closed';
  setterNote: string | null;
  reason: string | null;                     // why closed (greyed out, not selectable)
}

// =====================================================================================================================
// Searches (§4, §10, §12)
// =====================================================================================================================

export interface LeadSearch {
  id: number;
  nicheKey: string;
  nicheVersion: number;
  mode: SearchMode;
  offer: LeadOffer;                          // sorts results; scores exist for all three
  country: Country;
  areas: SearchArea[];
  terms: string[];
  limitPerTerm: number;
  status: SearchStatus;
  stage: SearchStage | null;
  estimate: SearchEstimate | null;
  budgetUsd: number;
  spentUsd: number;                          // Σ ledger (reserved + settled)
  funnel: SearchFunnel | null;
  report: SearchReport | null;               // smoke and pilot only
  error: string | null;
  createdBy: number;
  createdByName: string | null;
  createdAt: ISODate;
  startedAt: ISODate | null;
  finishedAt: ISODate | null;
  /** S1 vendor pinned at creation (0019). Absent only on hand-built objects; a row without one is a pre-0019 Apify search. */
  discoveryVendor?: DiscoveryVendor;
  /** SPEC §19: the newest owner-approval request for this search (list and detail routes only). */
  approval?: SearchApproval | null;
}

/** A search's spend request, as the Searches tab shows it ("Waiting for PJ's approval", declined with the note). */
export interface SearchApproval {
  requestId: number;
  status: 'pending' | 'approved' | 'declined' | 'canceled';
  requestedByName: string;
  requestedAt: ISODate;
  budgetUsd: number | null;
  decidedByName: string | null;
  decidedAt: ISODate | null;
  note: string | null;
}

export interface SearchCreateRequest {
  nicheKey: string;
  nicheVersion: number;
  mode: Exclude<SearchMode, 'single'>;       // 'single' is created by POST /api/leadgen/research/:businessId
  offer: LeadOffer;
  country: Country;
  areas: SearchArea[];
  terms: string[];                           // subset of the profile's search_terms
  limitPerTerm: number;                      // smoke is forced to 3, pilot to 60
}
export interface SearchRunRequest { budgetUsd: number; confirmHandPick: boolean }
export interface SearchPatchRequest { budgetUsd: number }
/** 409 'search_blocked' (run) carries details: SearchBlocked; the strings are plain sentences for PJ. */
export interface SearchBlocked { blockers: string[] }

export interface StageProgress {
  stage: SearchStage;
  label: string;
  queued: number; running: number; waiting: number; done: number; failed: number; canceled: number;
  failedByKind: Partial<Record<LeadTaskKind, number>>;
}
export interface VendorSpend { vendor: LedgerVendor; estUsd: number; actualUsd: number; reservedUsd: number; settledUsd: number }
export interface SearchDetail {
  search: LeadSearch;
  funnel: SearchFunnel;
  stages: StageProgress[];
  spendByVendor: VendorSpend[];
  errors: string[];                          // plain words, newest first
  redLines: string[];                        // "PageSpeed failed on 41 of 612 sites: quota" (> 5% failed in a stage)
}

export interface LeadHookSummary { id: number; type: HookType; usage: HookUsage; strength: number; sayText: string; evidenceUrl: string; openerEligible: boolean }
export interface OfferScore { fit: number; reach: number; hookStrength: number; priority: number; tier: Tier }

/** One lead_places row for the Results table (§10 tab 4). */
export interface LeadRow {
  id: number;                                // lead_places.id
  placeId: string;
  status: LeadPlaceStatus;
  tier: Tier | null;
  priority: number | null;
  name: string;
  city: string | null;
  stateProv: string | null;
  country: Country | null;
  categoryName: string | null;
  topReasons: ScoreReason[];                 // top 3 for the search's offer
  scores: Partial<Record<LeadOffer, OfferScore>>;
  hook: LeadHookSummary | null;              // primary hook for the search's offer
  flags: LeadFlag[];
  dropGate: DropGate | null;
  dropDetail: string | null;
  phoneE164: string | null;
  phoneDisplay: string | null;
  website: string | null;
  mapsUrl: string;                           // the row's source_url
  existingBusinessId: number | null;
  businessId: number | null;                 // set once imported
  note: string | null;                       // e.g. 'held: hand-pick cap'
}
export interface LeadResultsQuery {
  tier?: Tier; status?: LeadPlaceStatus; state?: string; flag?: LeadFlag; drop?: DropGate; hasHook?: boolean;
  page?: number; pageSize?: number;          // page is 1-based; pageSize <= 200
}
/** §10 tab 4 Export CSV columns, in order. */
export const LEAD_CSV_COLUMNS = [
  'name', 'phone', 'phone_e164', 'website', 'city', 'state', 'country', 'tier', 'priority', 'top_reasons', 'hook',
  'hook_evidence_url', 'source_url',
] as const;

export interface SearchReviewRequest {
  approve?: number[] | { tiers: Tier[] };    // lead_places ids, or every scored row in those tiers ('D' only when ticked)
  reject?: Array<{ id: number; reason: string }>;
}
export interface SearchReviewResponse { approved: number; rejected: number; skipped: number }
export interface SearchImportRequest { campaignId: number }
export interface SearchImportResponse extends ImportReport {
  searchId: number;
  heldHandPick: Array<{ state: string; count: number }>;   // approved rows left out by leadgen_hand_pick_cap_per_search
}

// =====================================================================================================================
// Smoke and pilot reports (§5.4, §6.2), stored in lead_searches.report_json
// =====================================================================================================================

/** Check keys are slots, not vendors: on a DataForSEO-discovery smoke, `webhook` = every live discovery request answered,
 *  `apify_fields` = place_id, cid, phone, is_claimed on 3+ rows, `opening_hours` = work_hours present, `charge_cap` = every
 *  discovery request reserved first and billed no more than its reservation. The label carries the vendor's wording. */
export type SmokeCheckKey =
  | 'webhook' | 'apify_fields' | 'opening_hours' | 'charge_cap' | 'dfs_reviews' | 'rank_check_url' | 'psi'
  | 'twilio' | 'anthropic_batch' | 'ledger_accuracy' | 'places_no_writes';
export interface SmokeCheck { key: SmokeCheckKey; label: string; pass: boolean; value: string }   // value = the real observed value
export interface SmokeReport {
  kind: 'smoke';
  searchId: number;
  nicheKey: string;
  nicheVersion: number;
  passed: boolean;                           // every check passed
  checks: SmokeCheck[];                      // all 11 of §5.4, in order
  estimateUsd: number;
  actualUsd: number;
  generatedAt: ISODate;
}

export interface HeadToHead {                // §4.6 (Apify vs the DataForSEO rank-grid places, same city)
  city: string;
  apifyPlaces: number; dfsPlaces: number; overlap: number;
  apifyPhoneFill: number | null; dfsPhoneFill: number | null;
  apifyDomainFill: number | null; dfsDomainFill: number | null;
  claimedAgreement: number | null;           // share where is_claimed agrees with claimThisBusiness
  dfsCoverageOfApifyInNiche: number | null;  // switch needs >= 0.90 plus fill within 5 points
}
export interface PilotReport {
  kind: 'pilot';
  searchId: number;
  nicheKey: string;
  nicheVersion: number;
  inNiche: { checked: number; correct: number; wrong: number; unsure: number; precision: number | null };
  labelsSeen: Array<{ label: string; count: number; onAllow: boolean; onDeny: boolean }>;
  chainGroups: Array<{ key: string; size: number; names: string[] }>;
  noSiteShare: number | null;
  weekdayCloseHistogram: Array<{ close: string; count: number }>;   // 'HH:MM'
  detectorHitRates: Array<{ detector: string; hits: number; measured: number }>;
  mobileLineShare: number | null;
  measured: { k: number | null; s: number | null; regexHitRate: number | null; abRate: number | null; aboutPageRate: number | null };
  headToHead: HeadToHead | null;            // null when the pilot discovered with DataForSEO (nothing to compare against)
  generatedAt: ISODate;
}
export type SearchReport = SmokeReport | PilotReport;

// =====================================================================================================================
// Checks (gold sets, §7.3 and §10 tab 5)
// =====================================================================================================================

export interface CheckItem {
  detector: string;
  detectorVersion: number;
  nicheKey: string;
  placeId: string;
  name: string;
  city: string | null;
  detectorSaid: string;                      // 'positive' | 'negative' (+ value)
  claim: string;                             // what the detector says, in words
  evidenceUrl: string;
  screenshotUrl: string | null;              // signed R2 URL
  quote: string | null;                      // review quote, when the detector is review-based
  mapsUrl: string;
  progress: { positives: number; negatives: number; targetPositives: 30; targetNegatives: 10 };
}
export interface CheckNextResponse { item: CheckItem | null }
/** Up to n distinct unchecked places (pilot rows first) for "Check 30 at once" (in-niche precision). */
export interface CheckBatchResponse { items: CheckItem[] }
export interface CheckSubmitRequest {
  detector: string; detectorVersion: number; nicheKey: string; placeId: string; detectorSaid: string;
  verdict: CheckVerdict; note?: string;
}
export interface DetectorEval {
  detector: string;
  detectorVersion: number;
  nicheKey: string;
  positives: { correct: number; wrong: number; unsure: number };
  negatives: { correct: number; wrong: number; unsure: number };
  n: number;                                 // correct + wrong over positives (the precision denominator)
  precision: number | null;                  // null when n = 0
  openerEligible: boolean;                   // precision >= 0.95 with n >= 30
  scoringOn: boolean;                        // false when checked precision < 0.85
}
export interface CheckSubmitResponse { eval: DetectorEval }

// =====================================================================================================================
// Spend, suppression, keys (§10 tab 6)
// =====================================================================================================================

export interface KeyStatus {
  name: string;                              // env var name, e.g. 'APIFY_TOKEN'
  present: boolean;
  last4: string | null;                      // never more than the last 4 characters
  required: boolean;
  unlocks: string;                           // "Discovery (S1)"
}
export interface SpendReport {
  month: string;                             // 'YYYY-MM' in OPS_TZ
  capUsd: number;
  spentUsd: number;                          // reserved + settled, month to date
  byVendor: VendorSpend[];
  bySearch: Array<{ searchId: number; label: string; status: SearchStatus; estimateUsd: number | null; actualUsd: number; overEstimate25: boolean }>;
  liveChecks: { used: number; cap: number };
  keys: KeyStatus[];
}
export interface SuppressionEntry {
  kind: SuppressionKind; value: string; reason: string;
  createdBy: number | null; createdByName: string | null; createdAt: ISODate;
}
export interface SuppressionUpdateRequest {
  add?: Array<{ kind: SuppressionKind; value: string; reason: string }>;
  remove?: Array<{ kind: SuppressionKind; value: string }>;
}

// =====================================================================================================================
// Calibration (§14)
// =====================================================================================================================

export interface ListHealth {
  dialled: number;
  wrongNumberRate: number | null;            // target < 0.03
  notAFitRate: number | null;                // target < 0.05
  closedRate: number | null;                 // target < 0.01
  breaches: string[];                        // plain words; any breach blocks the next Full search in the niche
}
export interface CalibrationReport {
  nicheKey: string;
  weightsVersion: string;
  generatedAt: ISODate;
  tiers: Array<{ tier: Tier; dials: number; dmConversations: number; meetings: number; dmPer100Dials: number | null; meetingsPer100Dm: number | null; meetingsPer100Dials: number | null }>;
  signals: Array<{
    signal: string; presentDm: number; absentDm: number; presentMeetings: number; absentMeetings: number;
    lift: number | null; ci90: [number, number] | null; proposal: 'halve' | 'raise' | null;
  }>;
  objectionRateByFdBand: Array<{ band: string; dmConversations: number; haveSomeone: number; rate: number | null }>;
  listHealth: ListHealth;
  killRule: { evaluated: boolean; tierAPer100: number | null; tierCPer100: number | null; ratio: number | null; triggered: boolean };
}

// =====================================================================================================================
// Research, Apollo, tools
// =====================================================================================================================

/** Owner (or a research already running): the search. Anyone else (SPEC §19): HTTP 202 with searchId null and the spend
 *  request waiting for PJ (`approval`, `message`). */
export interface ResearchResponse {
  searchId: number | null;
  status: SearchStatus | null;
  approval?: { id: number; status: 'pending' | 'approved' | 'declined' | 'canceled' };
  message?: string;
}
export interface ApolloRevealRequest { placeId: string; personRef: string }
/** Stored in lead_emails with source 'apollo'; never copied to businesses.email. */
export interface ApolloRevealResponse { email: string | null; creditsUsed: number }

export interface ToolsSmokeRequest {
  niche: string;                             // profile key, e.g. 'plumbing_hvac'
  nicheVersion?: number;                     // default: the newest approved version
  city: string;                              // "Hamilton, ON, Canada"
  limit: number;                             // 3 for a smoke
  live: boolean;                             // false = dry run: estimate only, nothing created or spent
}
export interface ToolsSmokeResponse { dryRun: boolean; searchId: number | null; estimate: SearchEstimate }

// =====================================================================================================================
// §11 the in-call Business drawer
// =====================================================================================================================

/** Apify openingHours shape, e.g. [{day:'Monday', hours:'8 AM to 5 PM'}]. */
export type WeekHours = Array<{ day: string; hours: string }>;

export interface DrawerHook {
  id: number;                                // lead_hooks.id (sent back as CallPrepareRequest.hookId)
  type: HookType;
  offer: LeadOffer;
  usage: HookUsage;
  strength: number;
  sayText: string;
  slots: Record<string, string>;
  evidenceUrl: string;
  snippet: string | null;
  openerEligible: boolean;
  measuredAt: ISODate;
  expiresAt: ISODate;
  /** What a live check re-tests for this hook (empty = nothing live; the evidence link is enough). */
  liveDependsOn: Array<'site' | 'hours' | 'status' | 'website_link'>;
}

export interface MapRankEntry {
  term: string;                              // "plumber near me" | "Hamilton plumber"
  best: number | null;
  median: number | null;
  points: number;                            // grid points measured
  notInTop100: boolean;
  checkUrl: string;                          // DataForSEO check_url (evidence)
  measuredAt: ISODate;
}

export interface ReviewMention {
  reviewId: string;
  kind: 'complaint' | 'praise';
  detail: string | null;                     // no_answer | voicemail | no_callback | hard_to_reach | other
  rating: number | null;
  month: string;                             // 'YYYY-MM'
  publishedAt: ISODate;
  quote: string;                             // verbatim, checked in code; never a reviewer name
  reviewUrl: string;
}

export interface DrawerPerson {
  name: string;
  role: string | null;
  label: string;                             // 'Owner' | 'licence contact' | "signed replies as 'Mike'" | 'told us on a call'
  sourceKind: PersonSourceKind;
  sourceUrl: string;
  status: PersonStatus;
  measuredAt: ISODate;
  apolloRef: string | null;                  // for POST /api/leadgen/apollo/reveal (admins, Apollo on)
  emailAvailable: boolean | null;            // Apollo has_email; null when unknown
}

export interface DrawerEmail {
  email: string;
  sourceKind: EmailSourceKind;
  sourceUrl: string;
  onOwnDomain: boolean;
  verifyStatus: EmailVerifyStatus;
  usableForEmail: boolean;
  reason: string | null;                     // "not usable for email (CASL)"
}

export interface DrawerData {
  businessId: number | null;                 // null for a Results row not imported yet (GET /api/leadgen/places/:id/drawer)
  leadPlaceId: number | null;                // lead_places row it came from, when known
  membershipId: number | null;
  placeId: string | null;
  researched: boolean;                       // false: CSV lead with no snapshot (show the Research button)
  researchAt: ISODate | null;
  offer: LeadOffer | null;                   // engine offer for the membership's campaign (best of engineOffersFor)
  flags: LeadFlag[];
  generatedAt: ISODate;

  header: {
    name: string; city: string | null; stateProv: string; country: Country; tz: string;
    tier: Tier | null; priority: number | null;
    rating: number | null; reviewsCount: number | null; categoryName: string | null; extraCategories: number;
    mapsUrl: string | null;                  // Google Maps button (maps_url / gbp_url)
    website: string | null;
    searchResultsUrl: string | null;         // map_rank check_url
    hours: WeekHours | null;                 // snapshot; the UI prefers live hours when present
    hoursMeasuredAt: ISODate | null;
    // PJ 2026-10-09 ("Search the area", the Google Maps search fallback). Optional: absent on older servers.
    niche?: string | null;                   // the campaign's niche (else the search's niche key or the business's niche)
    address?: string | null;                 // street address from the source list (businesses.address, migration 0028)
  };
  hook: {
    primary: DrawerHook | null;
    backup: DrawerHook | null;
    dontSay: string[];                       // OPENER_NEVER words plus lead-flag lines
    none: string | null;                     // "No verified observation. Use the standard opener."
    observationVerifiedAt: ISODate | null;
  };
  map: {
    reviewsCount: number | null; newestReviewAt: ISODate | null; n12m: number | '50+' | null;
    ownerReplyRate20: number | null; hoursLine: string | null; claimed: boolean | null; claimedMeasuredAt: ISODate | null;
    ranks: MapRankEntry[]; measuredAt: ISODate | null;
  } | null;
  phoneMentions: { complaints: ReviewMention[]; praise: ReviewMention | null; all: ReviewMention[] };   // complaints <= 3
  website: {
    url: string; screenshotUrl: string | null; loads: boolean | null; checkedAt: ISODate | null;
    psiScore: number | null; lcpMs: number | null; psiMeasuredAt: ISODate | null;
    viewport: boolean | null; tel: boolean | null; bookingPath: string | null; menuPath: string | null; menuHost: string | null;
    tech: Array<{ vendor: string; host: string; url: string }>; builtOn: string | null; copyrightYear: number | null;
    ageCheck: boolean | null; hasWebVendor: boolean;
  } | null;
  people: DrawerPerson[];
  emails: DrawerEmail[];
  gatekeeperName: string | null;
  history: {
    calls: CallSummary[];                    // last 3
    attempts: number | null; maxAttempts: number | null; vmCount: number | null; maxVms: number | null;
    /** SPEC §19 lead recycling (migration 0018): the lead's latest recycle round; absent on older servers. */
    recycle?: RecycleRound | null;
    openCallbacks: Task[];
    meetings: Meeting[];
  };
  footer: {
    reasons: ScoreReason[];                  // why this tier, for `offer`
    scores: Partial<Record<LeadOffer, OfferScore>>;
    switchHint: string | null;               // another offer's fit >= 25 points higher
  };
  competitors: Array<{ name: string; rank: number; rating: number | null; reviewsCount: number | null }>;   // display-only
  socials: Array<{ kind: string; url: string }>;   // business socials from its own site only
  sources: Array<{ label: string; url: string; measuredAt: ISODate }>;
  live: { policy: LiveCheckPolicy; placesAvailable: boolean; placesReason: string | null };
  /** pendingRequestId (SPEC §19): a research request waiting for the owner's approval. */
  research: { available: boolean; estUsd: number; searchId: number | null; status: SearchStatus | null; pendingRequestId?: number | null };
}

export type LiveBusinessStatus = 'OPERATIONAL' | 'CLOSED_TEMPORARILY' | 'CLOSED_PERMANENTLY' | 'FUTURE_OPENING';
/** Places API (New) Place Details, live only (lane L). Lives in page memory; never written to D1, R2 or logs. */
export interface LivePlace {
  businessStatus: LiveBusinessStatus | null;
  openNow: boolean | null;
  nextCloseTime: ISODate | null;
  weekdayDescriptions: string[] | null;
  rating: number | null;
  userRatingCount: number | null;
  websiteUri: string | null;
  nationalPhoneNumber: string | null;
  googleMapsUri: string | null;
  reviewsUri: string | null;
  movedPlaceId: string | null;
}
export interface LiveSite { status: SiteStatus; finalUrl: string | null; https: boolean | null }

export interface LiveCheckRequest { parts: Array<'site' | 'places'>; hookId?: number; membershipId?: number }
export interface LiveCheck {
  businessId: number;
  checkedAt: ISODate;
  attribution: 'Google Maps';
  places: { ok: true; data: LivePlace } | { ok: false; reason: string } | null;   // null = not requested
  site: { ok: true; data: LiveSite } | { ok: false; reason: string } | null;
  hookStatus: HookLiveStatus;
  hookStatusReason: string | null;           // "Google now shows a website link"
  banner: string | null;                     // red banner, e.g. "Google says this business is permanently closed."
  dispositionHint: DispositionCode | null;   // 'not_a_fit' when closed
  quota: { monthUsed: number; monthCap: number; userDayUsed: number; userDayCap: number };
}
